import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Mail, CreditCard, FileText, Plus, RefreshCw, Database, Download, X, FileCheck, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API } from "../config";
import { StatCard } from "../components/shared/StatCard";
import { AnalyticsDashboard } from "../components/AnalyticsDashboard";
import { SignatureStatsWidget } from "../components/admin/SignatureStatsWidget";
import { FunnelAnalyticsWidget } from "../components/admin/FunnelAnalyticsWidget";
import { SocChangesWidget } from "../components/admin/SocChangesWidget";
import { WalletRefreshHealthWidget } from "../components/admin/WalletRefreshHealthWidget";
import { WalletMassRefreshWidget } from "../components/admin/WalletMassRefreshWidget";
import { WalletTestPushWidget } from "../components/admin/WalletTestPushWidget";
import { WalletCredentialsLifecycleWidget } from "../components/admin/WalletCredentialsLifecycleWidget";
import { SystemHealthCheckWidget } from "../components/admin/SystemHealthCheckWidget";
import { SlaHealthWidget } from "../components/admin/SlaHealthWidget";
import { SlaAuditLogWidget } from "../components/admin/SlaAuditLogWidget";
import { SignatureErrorsWidget } from "../components/admin/SignatureErrorsWidget";
import { SystemHealthBadge } from "../components/admin/SystemHealthBadge";
import { CountryAuditWidget } from "../components/admin/CountryAuditWidget";
import { TechAlertPreferencesCard } from "../components/admin/TechAlertPreferencesCard";
import { GdprAuditDownloadsWidget } from "../components/admin/GdprAuditDownloadsWidget";
import { Customer360Widget } from "../components/admin/Customer360Widget";
import { WalletAdoptionWidget } from "../components/admin/WalletAdoptionWidget";
import { MismatchDetectionWidget } from "../components/admin/MismatchDetectionWidget";
import { SmsHealthWidget } from "../components/admin/SmsHealthWidget";
import { EmailHealthWidget } from "../components/admin/EmailHealthWidget";
import { SollecitoWidget } from "../components/admin/SollecitoWidget";
import { OrphanCustomersWidget } from "../components/admin/OrphanCustomersWidget";
import { SalesforceHealthWidget } from "../components/admin/SalesforceHealthWidget";
import { SalesforceCustomerSyncWidget } from "../components/admin/SalesforceCustomerSyncWidget";
import { SalesforceLivePulseAdminWidget } from "../components/admin/SalesforceLivePulseAdminWidget";
import { CommunicationsExportButton } from "../components/admin/CommunicationsExportButton";
import { AdminBackupPanel } from "../components/admin/AdminBackupPanel";
import { AdminStatusBreakdown } from "../components/admin/AdminStatusBreakdown";
import { LegalDocumentsWidget } from "../components/admin/LegalDocumentsWidget";
import { LegalQuickAccessButton } from "../components/admin/LegalQuickAccessButton";
import { GdprComplianceWidget } from "../components/admin/GdprComplianceWidget";
import { LegalCoverageGapsAlert } from "../components/admin/LegalCoverageGapsAlert";
import { SmsCreditWidget } from "../components/admin/SmsCreditWidget";
import { useAuth } from "../context/AuthContext";
import { MFASettingsCard, MFANudge } from "../components/shared/MFA";
import { SmsWelcomeEngagementWidget } from "../components/admin/SmsWelcomeEngagementWidget";
import { BuildAdoptionWidget } from "../components/admin/BuildAdoptionWidget";
import { PassRefreshHistoryWidget } from "../components/admin/PassRefreshHistoryWidget";
import { CountryFilterProvider } from "../contexts/CountryFilterContext";
import { InfraResourcesPanel } from "../components/shared/InfraResourcesPanel";
import { CountryFilterDropdown } from "../components/shared/CountryFilterDropdown";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle statistiche");
    } finally {
      setLoading(false);
    }
  };

  const seedDemoData = async () => {
    try {
      await axios.post(`${API}/seed-demo-data`);
      toast.success("Dati demo creati con successo");
      fetchStats();
    } catch (error) {
      toast.error("Errore nella creazione dei dati demo");
    }
  };

  // ─── Backup state + handlers ──────────────────────────────────────────
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupSchedule, setBackupSchedule] = useState(null);
  const [showBackupReminder, setShowBackupReminder] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupEmailing, setBackupEmailing] = useState(false);
  const [showBackupPanel, setShowBackupPanel] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [backupTab, setBackupTab] = useState("main");
  const [cloudConfig, setCloudConfig] = useState({ provider: "aws_s3", endpoint_url: "", bucket_name: "", access_key: "", secret_key: "", region: "eu-west-1", enabled: false });
  const [savingCloud, setSavingCloud] = useState(false);
  const [uploadingCloud, setUploadingCloud] = useState(false);
  // TelcaVoIP Cloud (pCloud-backed managed backup option).
  const [telcavoipCloudStatus, setTelcavoipCloudStatus] = useState(null);
  const [telcavoipCloudToggling, setTelcavoipCloudToggling] = useState(false);
  const [telcavoipCloudRunning, setTelcavoipCloudRunning] = useState(false);

  const fetchBackupStatus = async () => {
    try {
      const [statusRes, scheduleRes, historyRes, cloudRes, tcRes] = await Promise.all([
        axios.get(`${API}/backup/status`),
        axios.get(`${API}/backup/schedule`),
        axios.get(`${API}/backup/history`),
        axios.get(`${API}/backup/cloud-config`),
        axios.get(`${API}/backup/telcavoip-cloud/status`).catch(() => ({ data: null })),
      ]);
      setBackupStatus(statusRes.data);
      setBackupSchedule(scheduleRes.data);
      setBackupHistory(historyRes.data);
      if (tcRes.data) setTelcavoipCloudStatus(tcRes.data);
      setCloudConfig(prev => ({ ...prev, ...cloudRes.data }));
      if (statusRes.data.needs_backup) {
        const dismissed = localStorage.getItem('backup_reminder_dismissed');
        if (!dismissed || Date.now() - parseInt(dismissed) > 24 * 60 * 60 * 1000) {
          setShowBackupReminder(true);
        }
      }
    } catch (e) { /* silent */ }
  };

  const downloadBackup = async () => {
    try {
      setBackupDownloading(true);
      const res = await axios.get(`${API}/backup/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lojack_backup_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Backup scaricato con successo");
      setShowBackupReminder(false);
      localStorage.setItem('backup_reminder_dismissed', Date.now().toString());
      fetchBackupStatus();
    } catch (error) {
      toast.error("Errore nel download del backup");
    } finally {
      setBackupDownloading(false);
    }
  };

  const sendBackupEmail = async () => {
    try {
      setBackupEmailing(true);
      const res = await axios.post(`${API}/backup/send-email`);
      toast.success(`Backup inviato a ${res.data.email}`);
      setShowBackupReminder(false);
      localStorage.setItem('backup_reminder_dismissed', Date.now().toString());
      fetchBackupStatus();
    } catch (error) {
      toast.error("Errore nell'invio del backup via email");
    } finally {
      setBackupEmailing(false);
    }
  };

  const toggleAutoBackup = async () => {
    try {
      const newState = !backupSchedule?.enabled;
      const days = backupSchedule?.interval_days || 7;
      await axios.post(`${API}/backup/schedule?enabled=${newState}&interval_days=${days}`);
      setBackupSchedule(prev => ({ ...prev, enabled: newState }));
      toast.success(newState ? `Backup automatico attivato (ogni ${days} giorni)` : "Backup automatico disattivato");
    } catch (error) {
      toast.error("Errore nella configurazione del backup");
    }
  };

  const updateBackupInterval = async (days) => {
    try {
      await axios.post(`${API}/backup/schedule?enabled=true&interval_days=${days}`);
      setBackupSchedule({ enabled: true, interval_days: days });
      const labels = {1: 'giornaliero', 7: 'settimanale', 15: 'quindicinale', 30: 'mensile'};
      toast.success(`Backup automatico ${labels[days] || `ogni ${days} giorni`}`);
    } catch (error) {
      toast.error("Errore nella configurazione");
    }
  };

  const saveCloudConfig = async () => {
    setSavingCloud(true);
    try {
      await axios.post(`${API}/backup/cloud-config`, cloudConfig);
      toast.success("Configurazione cloud salvata");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSavingCloud(false); }
  };

  const uploadToCloud = async () => {
    setUploadingCloud(true);
    try {
      const res = await axios.post(`${API}/backup/upload-cloud`);
      if (res.data.error) { toast.error(res.data.error); }
      else { toast.success(res.data.message); fetchBackupStatus(); }
    } catch { toast.error("Errore upload cloud"); }
    finally { setUploadingCloud(false); }
  };

  const toggleTelcavoipCloud = async (enabled) => {
    setTelcavoipCloudToggling(true);
    try {
      const res = await axios.post(`${API}/backup/telcavoip-cloud/toggle?enabled=${enabled}`);
      if (res.data.ok) {
        toast.success(enabled ? "TelcaVoIP Cloud attivato" : "TelcaVoIP Cloud disattivato");
        fetchBackupStatus();
      } else {
        toast.error(res.data.message || "Errore");
      }
    } catch { toast.error("Errore aggiornamento TelcaVoIP Cloud"); }
    finally { setTelcavoipCloudToggling(false); }
  };

  const runTelcavoipCloudNow = async () => {
    setTelcavoipCloudRunning(true);
    try {
      const res = await axios.post(`${API}/backup/telcavoip-cloud/run`);
      if (res.data.ok) {
        toast.success(`Backup caricato (${res.data.size_kb} KB)`);
        fetchBackupStatus();
      } else {
        toast.error(res.data.message || res.data.error || "Errore upload");
      }
    } catch { toast.error("Errore upload TelcaVoIP Cloud"); }
    finally { setTelcavoipCloudRunning(false); }
  };

  useEffect(() => { fetchBackupStatus(); }, []);
  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="spinner"></div></div>;
  }

  return (
    <CountryFilterProvider>
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="admin-dashboard">
      <MFANudge
        authHeader={{ Authorization: `Bearer ${localStorage.getItem('lojack_token') || ''}` }}
        userEmail={user?.email}
        lang="it"
      />
      <LegalCoverageGapsAlert />
      {["super_admin", "admin"].includes(user?.role) && <div className="mb-6"><InfraResourcesPanel compact canEdit={user?.role === "super_admin"} /></div>}
      <SmsCreditWidget />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">Monitoraggio Secure Wallet</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <SystemHealthBadge />
          <CountryFilterDropdown />
          <LegalQuickAccessButton />
          {user?.role === "super_admin" && (
            <button
              onClick={() => navigate("/admin/audit-dashboard")}
              className="btn-lojack-outline flex items-center gap-2 text-sm"
              data-testid="audit-dashboard-btn"
              title="Audit cross-collection (country policy, GDPR consents, dealer roles)"
            >
              <ShieldCheck size={16} />Audit Dashboard
            </button>
          )}
          {user?.role === "super_admin" && (
            <button
              onClick={() => navigate("/admin/audit/customer-overrides")}
              className="btn-lojack-outline flex items-center gap-2 text-sm"
              data-testid="customer-overrides-audit-btn"
              title="Audit override admin su clienti firmati o in auto-compilazione (GDPR Art. 16)"
            >
              <ShieldCheck size={16} />Override Clienti
            </button>
          )}
          <button onClick={() => setShowBackupPanel(!showBackupPanel)} className="btn-lojack-outline flex items-center gap-2 text-sm" data-testid="backup-btn">
            <Database size={16} />Backup DB
          </button>
          <button onClick={fetchStats} className="btn-lojack-outline flex items-center gap-2 text-sm" data-testid="refresh-btn">
            <RefreshCw size={16} />Aggiorna
          </button>
          <button onClick={seedDemoData} className="btn-lojack flex items-center gap-2 text-sm" data-testid="seed-data-btn">
            <Plus size={16} />Demo Data
          </button>
        </div>
      </div>

      {showBackupReminder && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm animate-fade-in" data-testid="backup-reminder">
          <div className="bg-[#1A1A1A] border border-lojack-clause-600/40 rounded-xl p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-lojack-clause-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Database className="w-5 h-5 text-lojack-clause-500" />
              </div>
              <div className="flex-1">
                <h4 className="text-white font-semibold text-sm mb-1">Backup Consigliato</h4>
                <p className="text-gray-400 text-xs mb-3">
                  {backupStatus?.last_backup ? `Ultimo backup: ${backupStatus.days_ago} giorni fa` : 'Non hai ancora eseguito un backup'}
                </p>
                <div className="flex gap-2">
                  <button onClick={downloadBackup} className="bg-lojack-red hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                    <Download size={12} />Scarica
                  </button>
                  <button onClick={() => { setShowBackupReminder(false); localStorage.setItem('backup_reminder_dismissed', Date.now().toString()); }} className="text-gray-500 hover:text-gray-300 text-xs px-3 py-1.5 transition-colors">Dopo</button>
                </div>
              </div>
              <button onClick={() => { setShowBackupReminder(false); localStorage.setItem('backup_reminder_dismissed', Date.now().toString()); }} className="text-gray-600 hover:text-gray-400"><X size={16} /></button>
            </div>
          </div>
        </div>
      )}

      <AdminBackupPanel
        show={showBackupPanel}
        onClose={() => setShowBackupPanel(false)}
        backupTab={backupTab}
        setBackupTab={setBackupTab}
        backupStatus={backupStatus}
        backupSchedule={backupSchedule}
        backupHistory={backupHistory}
        cloudConfig={cloudConfig}
        setCloudConfig={setCloudConfig}
        stats={stats}
        backupDownloading={backupDownloading}
        backupEmailing={backupEmailing}
        savingCloud={savingCloud}
        uploadingCloud={uploadingCloud}
        downloadBackup={downloadBackup}
        sendBackupEmail={sendBackupEmail}
        toggleAutoBackup={toggleAutoBackup}
        updateBackupInterval={updateBackupInterval}
        saveCloudConfig={saveCloudConfig}
        uploadToCloud={uploadToCloud}
        telcavoipCloudStatus={telcavoipCloudStatus}
        telcavoipCloudToggling={telcavoipCloudToggling}
        telcavoipCloudRunning={telcavoipCloudRunning}
        toggleTelcavoipCloud={toggleTelcavoipCloud}
        runTelcavoipCloudNow={runTelcavoipCloudNow}
      />


      <div className="mb-8">
        <SignatureStatsWidget />
      </div>

      <div className="mb-8">
        <WalletAdoptionWidget />
      </div>

      <div className="mb-8">
        <FunnelAnalyticsWidget />
      </div>

      <div className="mb-8">
        <SocChangesWidget />
      </div>

      <div className="mb-8">
        <WalletRefreshHealthWidget />
      </div>

      <div className="mb-8">
        <WalletMassRefreshWidget />
      </div>

      <div className="mb-8">
        <WalletTestPushWidget />
      </div>

      <div className="mb-8">
        <WalletCredentialsLifecycleWidget />
      </div>

      <div className="mb-8">
        <SystemHealthCheckWidget />
      </div>

      <div id="sla-health" className="mb-8">
        <SlaHealthWidget />
      </div>

      <div className="mb-8">
        <SlaAuditLogWidget />
      </div>

      <div className="mb-8">
        <SignatureErrorsWidget />
      </div>

      <div className="mb-8">
        <CountryAuditWidget />
      </div>

      <div className="mb-8">
        <GdprAuditDownloadsWidget />
      </div>

      <div className="mb-8">
        <Customer360Widget />
      </div>

      <div className="mb-8">
        <MismatchDetectionWidget />
      </div>

      <div className="mb-8">
        <SollecitoWidget />
      </div>

      <div className="mb-8">
        <OrphanCustomersWidget />
      </div>

      <div className="mb-8">
        <LegalDocumentsWidget />
      </div>

      <div className="mb-8">
        <GdprComplianceWidget />
      </div>

      <div className="grid grid-cols-1 gap-6 mb-8">
        <SalesforceLivePulseAdminWidget />
        <SmsHealthWidget variant="admin" />
        <EmailHealthWidget variant="admin" />
        <SalesforceHealthWidget />
        <SalesforceCustomerSyncWidget />
        <div className="flex justify-end">
          <CommunicationsExportButton />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={Users} value={stats?.total_customers || 0} label="Clienti Totali" color="text-lojack-red" />
        <StatCard icon={Mail} value={stats?.emails_read || 0} label="Email Lette" color="text-lojack-blue-400" />
        <StatCard icon={CreditCard} value={stats?.cards_added_to_wallet || 0} label="Card nel Wallet" color="text-blue-400" />
        <StatCard icon={FileText} value={stats?.contracts_signed || 0} label="Contratti Firmati" color="text-purple-400" />
      </div>

      <AdminStatusBreakdown stats={stats} />

      <div className="my-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SmsWelcomeEngagementWidget days={30} />
        <BuildAdoptionWidget top={5} />
      </div>

      <div className="my-6">
        <PassRefreshHistoryWidget isSuperAdmin={user?.role === "super_admin"} />
      </div>

      <div className="my-6">
        <MFASettingsCard
          authHeader={{ Authorization: `Bearer ${localStorage.getItem('lojack_token') || ''}` }}
          userEmail={user?.email}
          lang="it"
        />
      </div>

      <div className="my-6">
        <TechAlertPreferencesCard />
      </div>

      <AnalyticsDashboard />
    </div>
    </CountryFilterProvider>
  );
};

export default AdminDashboard;
